// src/pages/public/ServiceDetailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../components/layout/MainLayout';
import { serviceService } from '../../services/service';
import { FaPhone, FaWhatsapp, FaEnvelope, FaStar, FaCheckCircle } from 'react-icons/fa';
import { X } from 'lucide-react';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  white:      '#ffffff',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
};

/* ══════════════════════════════════════════════════════
   useInView — fires ONCE, never resets
══════════════════════════════════════════════════════ */
const useInView = (threshold = 0.12) => {
  const ref       = useRef(null);
  const [inView, setInView] = useState(false);
  const triggered = useRef(false);
  useEffect(() => {
    if (triggered.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        setInView(true);
        obs.disconnect();
      }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper — entry only, no looping ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(32px)',
    left:  'translateX(-32px)',
    right: 'translateX(32px)',
    scale: 'scale(0.94)',
  };
  return (
    <div ref={ref} style={{
      opacity:    inView ? 1 : 0,
      transform:  inView ? 'none' : from[dir],
      transition: `opacity 0.65s cubic-bezier(0.16,1,0.3,1) ${delay}ms,
                   transform 0.65s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── ModalAnim — for modal mode, triggers immediately on mount ── */
const ModalAnim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  const from = {
    up:    'translateY(24px)',
    left:  'translateX(-24px)',
    right: 'translateX(24px)',
    scale: 'scale(0.95)',
  };
  return (
    <div style={{
      opacity:    ready ? 1 : 0,
      transform:  ready ? 'none' : from[dir],
      transition: `opacity 0.55s cubic-bezier(0.16,1,0.3,1),
                   transform 0.55s cubic-bezier(0.16,1,0.3,1)`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── FeatureCard ── */
const FeatureCard = ({ emoji, title, desc, animDelay, isModal }) => {
  const [hov, setHov] = useState(false);
  const A = isModal ? ModalAnim : Anim;
  return (
    <A delay={animDelay} dir="up">
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          padding: '16px 18px',
          background: hov ? C.amberLight : '#fafaf9',
          borderRadius: 14,
          border: `1.5px solid ${hov ? 'rgba(217,119,6,0.25)' : C.gray100}`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
          cursor: 'default',
          transform: hov ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: hov ? '0 10px 28px rgba(0,0,0,0.09)' : 'none',
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, background 0.25s ease, border-color 0.25s ease',
        }}
      >
        <span style={{
          fontSize: 24, flexShrink: 0,
          display: 'inline-block',
          transform: hov ? 'scale(1.25) rotate(-6deg)' : 'scale(1) rotate(0deg)',
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {emoji}
        </span>
        <div>
          <div style={{ fontWeight: 700, color: C.gray900, fontSize: 14, marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 12.5, color: C.gray500, lineHeight: 1.6 }}>{desc}</div>
        </div>
      </div>
    </A>
  );
};

/* ── TrustItem ── */
const TrustItem = ({ emoji, title, desc }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'default',
        transform: hov ? 'translateX(5px)' : 'translateX(0)',
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <span style={{
        fontSize: 20, flexShrink: 0,
        display: 'inline-block',
        transform: hov ? 'scale(1.18) rotate(6deg)' : 'scale(1) rotate(0deg)',
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        {emoji}
      </span>
      <div>
        <div style={{ fontWeight: 700, color: C.gray900, fontSize: 13.5 }}>{title}</div>
        <div style={{ fontSize: 12, color: C.gray500 }}>{desc}</div>
      </div>
    </div>
  );
};

/* ── CheckRow ── */
const CheckRow = ({ text }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '7px 12px', margin: '0 -12px 2px',
        borderRadius: 10,
        background: hov ? 'rgba(217,119,6,0.06)' : 'transparent',
        transform: hov ? 'translateX(6px)' : 'translateX(0)',
        transition: 'background 0.2s ease, transform 0.2s ease',
        cursor: 'default',
      }}
    >
      <FaCheckCircle style={{ color: C.amber, flexShrink: 0, marginTop: 2, width: 15, height: 15 }} />
      <span style={{ color: C.gray700, fontSize: 14 }}>{text}</span>
    </div>
  );
};

/* ── CTA Buttons ── */
const WaCtaBtn = ({ onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      width: '100%', padding: '14px', borderRadius: 50, border: 'none', cursor: 'pointer',
      background: 'linear-gradient(135deg, #16a34a, #15803d)', color: '#fff',
      fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
      boxShadow: hov ? '0 12px 28px rgba(22,163,74,0.42)' : '0 4px 14px rgba(22,163,74,0.28)',
      transform: hov ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
      transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <FaWhatsapp style={{ width: 18, height: 18 }} /> Book via WhatsApp
    </button>
  );
};

const CallCtaBtn = ({ onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      width: '100%', padding: '14px', borderRadius: 50, border: 'none', cursor: 'pointer',
      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff',
      fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
      boxShadow: hov ? '0 12px 28px rgba(37,99,235,0.38)' : '0 4px 14px rgba(37,99,235,0.24)',
      transform: hov ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
      transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <FaPhone style={{ width: 14, height: 14 }} /> Call Now
    </button>
  );
};

const EmailCtaBtn = ({ onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      width: '100%', padding: '14px', borderRadius: 50, cursor: 'pointer', fontFamily: 'inherit',
      border: `2px solid ${C.amber}`, background: 'transparent', color: C.amber,
      fontWeight: 700, fontSize: 15,
      transform: hov ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
      boxShadow: hov ? '0 12px 28px rgba(217,119,6,0.2)' : 'none',
      transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <FaEnvelope style={{ width: 14, height: 14 }} /> Send Email
    </button>
  );
};

const CloseBtn = ({ onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      position: 'absolute', top: 16, right: 16,
      width: 42, height: 42, borderRadius: '50%',
      background: hov ? C.gray100 : 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(8px)',
      border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 14px rgba(0,0,0,0.14)', zIndex: 10,
      transform: hov ? 'rotate(90deg) scale(1.1)' : 'rotate(0deg) scale(1)',
      transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <X size={17} color={C.gray700} />
    </button>
  );
};

/* ── useWindowWidth hook ── */
const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const ServiceDetailPage = ({
  service: propService,
  onClose,
  formatCurrency: propFormatCurrency,
  getServiceIcon: propGetServiceIcon,
  isModal = false,
}) => {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [service, setService]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [mousePos, setMousePos]   = useState({ x: 0, y: 0 });

  const fmtCurrency = propFormatCurrency || ((amount) => {
    if (!amount) return '-';
    return `IDR ${new Intl.NumberFormat('id-ID').format(amount)}`;
  });

  const getIcon = propGetServiceIcon || ((type) => {
    const map = { transport: '🚗', tour: '🗺️', equipment: '⚙️', beauty: '💄', spa: '🧘', sport: '⚽', other: '🎯' };
    return map[type] || '📋';
  });

  const serviceTypes = {
    transport: 'Transportation', tour: 'Tour & Travel',
    equipment: 'Equipment Rental', beauty: 'Beauty Services',
    spa: 'Spa & Wellness', sport: 'Sports & Activities', other: 'Other Services',
  };

  useEffect(() => {
    if (isModal) return;
    const onMouse = e => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => window.removeEventListener('mousemove', onMouse);
  }, [isModal]);

  useEffect(() => {
    if (propService && isModal) { setService(propService); setLoading(false); return; }
    if (id && id !== 'undefined') { fetchDetail(); return; }
    setLoading(false);
    if (!isModal) { toast.error('Invalid Service ID'); navigate('/services'); }
  }, [id, propService, isModal]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await serviceService.getById(id);
      if (res?.data) setService(res.data);
      else { toast.error('Service not found'); if (!isModal) navigate('/services'); }
    } catch { toast.error('Failed to load'); if (!isModal) navigate('/services'); }
    finally { setLoading(false); }
  };

  const handleWhatsApp = () => {
    const phone = '6287785686777';
    const msg = `Hello! I'm interested in the "${service.title}" service.\n\nType: ${serviceTypes[service.service_type] || 'Service'}\n${service.price_idr ? `Price: ${fmtCurrency(service.price_idr)}` : 'Price: Contact for info'}\n\nPlease provide more information. Thank you!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };
  const handleCall  = () => { if (service.phone) window.location.href = `tel:${service.phone}`; };
  const handleEmail = () => {
    const sub  = `Inquiry: ${service.title}`;
    const body = `Hello,\n\nI'm interested in the "${service.title}" service.\n\nPlease provide more information.\n\nThank you.`;
    window.location.href = `mailto:info@balibuildproperty.com?subject=${encodeURIComponent(sub)}&body=${encodeURIComponent(body)}`;
  };
  const handleClose         = () => { if (onClose) onClose(); else navigate('/services'); };
  const handleBackdropClick = e  => { if (e.target === e.currentTarget) handleClose(); };
  const getImgUrl           = p  => { if (!p) return null; if (p.startsWith('http')) return p; return p.startsWith('/') ? p : `/${p}`; };

  const features = [
    { emoji: '✅', title: 'Professional & Experienced', desc: 'Expert team with years of experience in Bali' },
    { emoji: '⚡', title: 'Quick Response',             desc: 'Fast and efficient service delivery' },
    { emoji: '💯', title: 'Quality Guaranteed',         desc: 'Highest service standards maintained' },
    { emoji: '💰', title: 'Competitive Pricing',        desc: 'Best value for your investment' },
  ];

  const checklist = [
    'Prices are subject to change based on request',
    'Custom packages available based on your needs',
    'Free initial consultation included',
    'Customer satisfaction fully guaranteed',
  ];

  /* ── Wrapper animation component depending on mode ── */
  const A = isModal ? ModalAnim : Anim;

  /* ── Loading ── */
  if (loading) {
    const spinner = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isModal ? 280 : '60vh' }}>
        <div style={{ width: 50, height: 50, border: '4px solid #fef3c7', borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
    if (isModal) return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={handleBackdropClick}>
        <div style={{ background: C.white, borderRadius: 24, padding: 48 }}>{spinner}</div>
      </div>
    );
    return <MainLayout><div style={{ minHeight: '60vh' }}>{spinner}</div></MainLayout>;
  }

  /* ── Not found ── */
  if (!service) {
    const notFound = (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: C.gray900, marginBottom: 12 }}>Service not found</h2>
        <button onClick={handleClose} style={{ padding: '12px 28px', borderRadius: 50, border: 'none', cursor: 'pointer', backgroundColor: C.amber, color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'inherit' }}>
          Back to Services
        </button>
      </div>
    );
    if (isModal) return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }} onClick={handleBackdropClick}>
        <div style={{ background: C.white, borderRadius: 24 }}>{notFound}</div>
      </div>
    );
    return <MainLayout><div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{notFound}</div></MainLayout>;
  }

  /* ══ CONTENT ══ */
  const Content = () => {
    const winWidth = useWindowWidth();
    const isMobile = winWidth < 768;
    const isTablet = winWidth < 1024;
    return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: C.stone50 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        /* ── Entry animations only — NO looping animations ── */
        @keyframes spin      { to { transform: rotate(360deg); } }
        @keyframes imgReveal { from { opacity: 0; transform: scale(1.04); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalUp   { from { opacity: 0; transform: translateY(32px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }

        /* shimmer — only on standalone page, not modal */
        @keyframes shimmer   { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        /* In modal mode, disable shimmer looping */
        .shimmer-static {
          color: ${C.amber};
          font-weight: 700;
        }

        .detail-hero-img { animation: imgReveal 0.9s cubic-bezier(0.16,1,0.3,1) both; }
        .img-wrap img    { transition: transform 0.9s cubic-bezier(0.16,1,0.3,1); }
        .img-wrap:hover img { transform: scale(1.04); }
      `}</style>

      {/* ── Hero Image ── */}
      <div style={{ position: 'relative', background: C.gray900 }}>
        <div className="img-wrap" style={{ position: 'relative', height: isModal ? (isMobile ? 220 : 340) : (isMobile ? 260 : 500), overflow: 'hidden' }}>
          {service.image_path ? (
            <>
              <img
                className="detail-hero-img"
                src={getImgUrl(service.image_path)}
                alt={service.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.5s ease' }}
                onLoad={() => setImgLoaded(true)}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0.08) 50%, transparent)', pointerEvents: 'none' }} />
            </>
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fef3c7, #fed7aa)', fontSize: 90 }}>
              {getIcon(service.service_type)}
            </div>
          )}

          {/* Close button — modal only */}
          {isModal && <CloseBtn onClick={handleClose} />}

          {/* Featured badge — entry animation only */}
          {(service.featured === 1 || service.featured === true) && (
            <div style={{
              position: 'absolute', top: 16, left: 16,
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', background: C.amber, color: '#fff',
              borderRadius: 50, fontSize: 12, fontWeight: 700,
              boxShadow: '0 4px 12px rgba(217,119,6,0.45)',
              animation: 'slideDown 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.2s both',
            }}>
              {/* Static dot — no pulsing in modal */}
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
              Featured Service
            </div>
          )}

          {/* Service type badge */}
          <div style={{
            position: 'absolute', bottom: 20, left: 20,
            animation: 'slideDown 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.35s both',
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 16px',
              background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.28)', borderRadius: 50,
              color: '#fff', fontSize: 13, fontWeight: 600,
            }}>
              <span style={{ fontSize: 17 }}>{getIcon(service.service_type)}</span>
              {serviceTypes[service.service_type] || 'Service'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1140, margin: '0 auto', padding: isMobile ? '24px 16px 40px' : '40px 24px 56px', position: 'relative' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : 'minmax(0,1fr) 320px'),
          gap: isMobile ? 20 : 36,
          alignItems: 'flex-start',
        }}>

          {/* ── LEFT ── */}
          <div style={{ minWidth: 0 }}>

            {/* Title card */}
            <A delay={0} dir="left">
              <div style={{
                background: C.white, borderRadius: 20, padding: isMobile ? '20px 18px' : '28px 30px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}`,
                marginBottom: 16, position: 'relative', overflow: 'hidden',
              }}>
                <h1 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: isMobile ? '28px' : 'clamp(26px,4vw,42px)', fontWeight: 700, color: C.gray900,
                  lineHeight: 1.15, marginBottom: 14,
                }}>
                  {service.title}
                </h1>
                {service.phone && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 9,
                    padding: '9px 18px', background: C.amberLight,
                    border: '1.5px solid rgba(217,119,6,0.22)', borderRadius: 50,
                  }}>
                    <FaPhone style={{ color: C.amber, width: 13, height: 13 }} />
                    <span style={{ fontWeight: 700, color: C.amberDark, fontSize: 14.5 }}>{service.phone}</span>
                  </div>
                )}
              </div>
            </A>

            {/* Description */}
            <A delay={80} dir="left">
              <div style={{
                background: C.white, borderRadius: 20, padding: isMobile ? '20px 18px' : '28px 30px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}`,
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 36, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
                  <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 20 : 24, fontWeight: 700, color: C.gray900 }}>About This Service</h2>
                </div>
                <div style={{ color: C.gray600, lineHeight: 1.85, fontSize: 15 }}>
                  {service.description
                    ? <div dangerouslySetInnerHTML={{ __html: service.description }} />
                    : <p>Professional service with the highest quality to meet all your needs in Bali.</p>}
                </div>
              </div>
            </A>

            {/* Highlights */}
            <A delay={140} dir="left">
              <div style={{
                background: C.white, borderRadius: 20, padding: isMobile ? '20px 18px' : '28px 30px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}`,
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{ width: 36, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
                  <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 20 : 24, fontWeight: 700, color: C.gray900 }}>Service Highlights</h2>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px,1fr))', gap: 10 }}>
                  {features.map((f, i) => (
                    <FeatureCard key={i} emoji={f.emoji} title={f.title} desc={f.desc} animDelay={i * 60} isModal={isModal} />
                  ))}
                </div>
              </div>
            </A>

            {/* Checklist */}
            <A delay={200} dir="left">
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb, #fff7ed)',
                border: '2px solid rgba(217,119,6,0.18)', borderRadius: 20, padding: isMobile ? '20px 18px' : '24px 30px',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>💡</span>
                  <div style={{ width: '100%' }}>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 19 : 21, fontWeight: 700, color: C.gray900, marginBottom: 14 }}>
                      Important Information
                    </h3>
                    {checklist.map((item, i) => <CheckRow key={i} text={item} />)}
                  </div>
                </div>
              </div>
            </A>
          </div>

          {/* ── RIGHT — booking card ── */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top: isModal ? 0 : 24 }}>

            <A delay={100} dir={isMobile ? 'up' : 'right'}>
              <div style={{
                background: C.white, borderRadius: 22, padding: isMobile ? '20px 18px' : '26px 24px',
                boxShadow: '0 10px 44px rgba(0,0,0,0.11)',
                border: '2px solid rgba(217,119,6,0.18)',
                marginBottom: 14, position: 'relative', overflow: 'hidden',
                transition: 'box-shadow 0.35s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 24px 60px rgba(0,0,0,0.14)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 10px 44px rgba(0,0,0,0.11)'}
              >
                {/* Price */}
                <div style={{ marginBottom: 18 }}>
                  {service.price_idr && service.price_idr > 0 ? (
                    <>
                      {/* Static amber price in modal, shimmer on standalone */}
                      <div className={isModal ? 'shimmer-static' : 'shimmer-text'}
                        style={isModal ? { fontFamily: "'Cormorant Garamond', serif", fontSize: 34, fontWeight: 700, color: C.amber } : { fontFamily: "'Cormorant Garamond', serif", fontSize: 34, fontWeight: 700 }}>
                        {fmtCurrency(service.price_idr)}
                      </div>
                      <div style={{ fontSize: 11, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}>Starting from</div>
                      {service.price_usd > 0 && (
                        <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>~USD {service.price_usd?.toLocaleString()}</div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: C.gray700 }}>
                      Contact for Pricing
                    </div>
                  )}
                </div>

                {/* Rating — static, no animation */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  marginBottom: 22, padding: '9px 14px',
                  background: C.amberLight, borderRadius: 12,
                }}>
                  {[1,2,3,4,5].map(s => <FaStar key={s} style={{ color: '#fbbf24', width: 14, height: 14 }} />)}
                  <span style={{ fontWeight: 700, color: C.gray900, fontSize: 14 }}>5.0</span>
                  <span style={{ color: C.gray500, fontSize: 13 }}>Excellent</span>
                </div>

                {/* CTA buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18 }}>
                  <WaCtaBtn    onClick={handleWhatsApp} />
                  {service.phone && <CallCtaBtn onClick={handleCall} />}
                  <EmailCtaBtn onClick={handleEmail} />
                </div>

                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 12, color: C.gray400, marginBottom: 3 }}>⚡ Quick response within 5 minutes</p>
                  <p style={{ fontSize: 12, color: C.gray400 }}>💬 Free consultation available</p>
                </div>
              </div>
            </A>

            {/* Trust badges */}
            <A delay={180} dir={isMobile ? 'up' : 'right'}>
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb, #fff7ed)',
                borderRadius: 18, padding: isMobile ? '18px 18px' : '20px 22px',
                border: '1.5px solid rgba(217,119,6,0.18)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: C.gray900, marginBottom: 14, textAlign: 'center' }}>
                  Why Choose Us
                </h3>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 10 : 11 }}>
                  <TrustItem emoji="✅" title="Verified Service"    desc="Trusted and certified provider" />
                  <TrustItem emoji="🛡️" title="Secure Transaction" desc="Protected payment process" />
                  <TrustItem emoji="🌟" title="Premium Support"     desc="24/7 customer assistance" />
                </div>
              </div>
            </A>
          </div>
        </div>
      </div>
    </div>
    );
  };

  /* ══ RENDER ══ */
  if (isModal) {
    return (
      <>
        <style>{`
          @keyframes backdropIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes modalUp    { from { opacity: 0; transform: translateY(36px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        `}</style>

        {/* Backdrop — separate div, no animation that causes repaint */}
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 9998,
            animation: 'backdropIn 0.2s ease both',
          }}
          onClick={handleClose}
        />

        {/* Modal container — centered, does NOT propagate clicks to backdrop */}
        <div
          style={{
            position: 'fixed', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '8px',
            pointerEvents: 'none', // let backdrop catch outside clicks
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.white, borderRadius: 18,
              maxWidth: 1060, width: '100%',
              maxHeight: '96vh', overflowY: 'auto',
              boxShadow: '0 40px 100px rgba(0,0,0,0.28)',
              animation: 'modalUp 0.4s cubic-bezier(0.34,1.56,0.64,1) both',
              pointerEvents: 'all', // re-enable inside modal
            }}
          >
            <Content />
          </div>
        </div>
      </>
    );
  }

  return (
    <MainLayout>
      {/* Cursor glow — standalone page only */}
      <div style={{
        pointerEvents: 'none', position: 'fixed',
        width: 260, height: 260, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%)',
        transform: 'translate(-50%,-50%)',
        left: mousePos.x, top: mousePos.y,
        transition: 'left 0.12s ease, top 0.12s ease',
        zIndex: 9999, mixBlendMode: 'multiply',
      }} />
      <Content />
    </MainLayout>
  );
};

export default ServiceDetailPage;